import React, { useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { Modal, Button, Table } from 'react-bootstrap';
import moment from 'moment';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import ViewVisitorRequest from './ViewVisitorRequest';

// DTOs
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface GetVisitorsResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO[];
  total: number;
}

interface RejectedModuleProps {
  show: boolean;
  onHide: () => void;
}

const statusColors: { [key: string]: string } = {
  REJECTED: 'badge bg-danger',
};

const RejectedModule: React.FC<RejectedModuleProps> = ({ show, onHide }) => {
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);

  const fetchVisitorRequests = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        const payload = { filterType: 'STATUS', filterValue: 'REJECTED', date: dateFilter };
        const res: AxiosResponse = await apiDefinitions.getVisitors(page.toString(), payload);
        if (res.status === 200) {
          const data: GetVisitorsResponse = res.data;
          setVisitorRequests(data.data || []);
          setTotal(data.total || 0);
        } else {
          setError(res.data?.statusDescription || 'Failed to fetch rejected visitor requests');
        }
      } catch (err: any) {
        setError('Failed to fetch rejected visitor requests. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [dateFilter]
  );

  useEffect(() => {
    if (show) {
      setPageNo(1); // Reset to page 1 when modal opens
      fetchVisitorRequests(1);
    }
  }, [show, dateFilter, fetchVisitorRequests]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(total / 15)) {
      setPageNo(newPage);
    }
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1); // Reset to page 1 on date change
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Rejected Visitor Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Filter by Date</label>
              <input
                type="date"
                className="form-control"
                value={dateFilter}
                onChange={handleDateFilterChange}
                disabled={loading}
              />
            </div>
          </div>

          {loading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && visitorRequests.length === 0 && (
            <div className="alert alert-info">No rejected visitor requests found.</div>
          )}

          {!loading && !error && visitorRequests.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Visitor Name</th>
                    <th>Company</th>
                    <th>Arrival Time</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitorRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.id}</td>
                      <td>{request.visitors[0]?.name || 'N/A'}</td>
                      <td>{request.visitors[0]?.visitorCompany || 'N/A'}</td>
                      <td>
                        {request.visitors[0]?.arrivalTime
                          ? moment(request.visitors[0].arrivalTime).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className={statusColors[request.status] || 'badge bg-secondary'}>
                          {request.status || 'N/A'}
                        </span>
                      </td>
                      <td>{moment(request.dateTime).format('YYYY-MM-DD HH:mm')}</td>
                      <td>
                        <div className="hstack gap-2 fs-3">
                          <Button
                            variant="icon btn-secondary rounded-circle btn-sm"
                            title="View Request"
                            onClick={() => handleViewClick(request.id)}
                          >
                            <i className="feather-eye" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-primary"
              disabled={pageNo === 1}
              onClick={() => handlePageChange(pageNo - 1)}
            >
              Previous
            </Button>
            <span>
              Page {pageNo} of {Math.ceil(total / 15) || 1}
            </span>
            <Button
              variant="outline-warning"
              disabled={pageNo >= Math.ceil(total / 15)}
              onClick={() => handlePageChange(pageNo + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedVisitorRequestId && (
        <ViewVisitorRequest
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          visitorRequestId={selectedVisitorRequestId}
        />
      )}
    </>
  );
};

export default RejectedModule;